import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useVoiceChatContext } from '../../contexts/VoiceChatContext';

const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <path d="M14 8L2 2l3.5 6L2 14l12-6z" fill="white"/>
  </svg>
);

const MicIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <rect x="5.5" y="1" width="5" height="7.5" rx="2.5" stroke="rgba(28,43,58,0.35)" strokeWidth="1.3"/>
    <path d="M3 7.5c0 2.76 2.24 5 5 5s5-2.24 5-5" stroke="rgba(28,43,58,0.35)" strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="8" y1="12.5" x2="8" y2="15" stroke="rgba(28,43,58,0.35)" strokeWidth="1.3" strokeLinecap="round"/>
    <line x1="5.5" y1="15" x2="10.5" y2="15" stroke="rgba(28,43,58,0.35)" strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const StopIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <rect x="1" y="1" width="10" height="10" rx="1.5" fill="white"/>
  </svg>
);

const RoleplayInputArea = ({ onSendMessage, disabled, placeholder = 'Your response' }) => {
  const [inputValue, setInputValue] = useState('');
  const voiceChat = useVoiceChatContext();
  const textareaRef = useRef(null);
  const wasRecording = useRef(false);
  const hasText = inputValue.trim().length > 0;

  // Mirror live transcript into input while recording
  useEffect(() => {
    if (voiceChat.isRecording) {
      wasRecording.current = true;
      if (voiceChat.transcript) {
        setInputValue(voiceChat.transcript);
      }
    } else if (wasRecording.current && !voiceChat.isRecording && !voiceChat.isProcessing) {
      wasRecording.current = false;
      const transcript = voiceChat.transcript;
      if (transcript && transcript.trim()) {
        onSendMessage(transcript.trim(), { isVoice: true });
        setInputValue('');
      } else {
        setInputValue('');
      }
    }
  }, [voiceChat.isRecording, voiceChat.transcript, voiceChat.isProcessing, onSendMessage]);

  const handleSubmit = () => {
    if (!hasText || disabled || voiceChat.isRecording) return;
    if (voiceChat.isSpeaking) voiceChat.stopSpeaking();
    onSendMessage(inputValue.trim(), { isVoice: false });
    setInputValue('');
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleButtonClick = async () => {
    if (voiceChat.isRecording) {
      await voiceChat.stopRecording();
    } else if (hasText) {
      handleSubmit();
    } else if (voiceChat.isSpeaking) {
      voiceChat.stopSpeaking();
    } else if (voiceChat.isConnected) {
      await voiceChat.startRecording();
    }
  };

  const autoResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const isProcessing = disabled || voiceChat.isProcessing;
  const isRecording  = voiceChat.isRecording;
  const isSpeaking   = voiceChat.isSpeaking;

  // Restore focus to the textarea when processing finishes (isSending → false).
  // The focus() in handleSubmit fires while the textarea is still disabled and is ignored.
  const prevProcessingRef = useRef(false);
  useEffect(() => {
    if (prevProcessingRef.current && !isProcessing && !isRecording) {
      textareaRef.current?.focus();
    }
    prevProcessingRef.current = isProcessing;
  }, [isProcessing, isRecording]);

  const btnClass = [
    'rp-send-btn',
    isRecording ? 'recording' : hasText && !isProcessing ? 'has-text' : !hasText && isSpeaking ? 'speaking' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="rp-input-area">
      <textarea
        ref={textareaRef}
        className="rp-textarea"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          if (voiceChat.isSpeaking) voiceChat.stopSpeaking();
        }}
        onKeyDown={handleKeyDown}
        onInput={autoResize}
        placeholder={placeholder}
        rows={1}
        disabled={isProcessing || isRecording}
      />
      <button
        type="button"
        className={btnClass}
        onClick={handleButtonClick}
        disabled={isProcessing && !isRecording}
        title={isRecording ? 'Stop and send response' : hasText ? 'Send' : isSpeaking ? 'Stop playback' : 'Start voice recording'}
      >
        {isRecording ? <StopIcon /> : hasText ? <SendIcon /> : isSpeaking ? <StopIcon /> : <MicIcon />}
      </button>
    </div>
  );
};

RoleplayInputArea.propTypes = {
  onSendMessage: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default RoleplayInputArea;
